import React, {useMemo} from 'react';
import PropTypes from "prop-types";
import {useFormik} from "formik";

import {useBookingItemCreate, useBookingItemDelete, useBookingItemUpdate} from "../../query";
import {ResponsiveDialog} from "../../../../components/ResponsiveDialog";
import {useFeedbackTools} from "../../../../components/FeedbackTools";
import bookingItemValidationSchema from "./validator";

import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";

import InputAdornment from "@mui/material/InputAdornment";


const FormBookingItem = (props) => {
    const {booking, bookingItem, accommodations, onClose} = props;
    const {showSuccessNotification, showErrorNotification} = useFeedbackTools();

    const bookingItemCreate = useBookingItemCreate({bookingId: booking.id});
    const bookingItemUpdate = useBookingItemUpdate({bookingId: booking.id});
    const bookingItemDelete = useBookingItemDelete({bookingId: booking.id});

    const isLoading = bookingItemCreate.isLoading || bookingItemUpdate.isLoading || bookingItemDelete.isLoading;
    const selectedAccommodationUnit = accommodations.findAccommodationUnit(bookingItem?.bookableType, bookingItem?.bookableId);

    // The options are the accommodation units that are not already selected in other booking items and the selected one (if any)
    const accommodationUnitOptions = accommodations.accommodationUnits.filter((unit) => !booking.bookingItems.some((item) => item.bookableId === unit.id && item.bookableType === unit.type));
    if(selectedAccommodationUnit){
        accommodationUnitOptions.push(selectedAccommodationUnit);
    }


    const formik = useFormik({
        initialValues: {
            accommodationUnit: selectedAccommodationUnit || null,
            price: bookingItem?.price || ''
        },
        validationSchema: bookingItemValidationSchema,
        onSubmit: async values => {
            // Note: arrival and departure will be set on server side
            const data = {
                bookableType: values.accommodationUnit.type,
                bookableId: values.accommodationUnit.id,
                price: values.price,
            }

            try{
                if(bookingItem.id !== null && bookingItem.id !== undefined ){
                    await bookingItemUpdate.mutateAsync({bookingItemId: bookingItem.id, data})
                }else {
                    await bookingItemCreate.mutateAsync({data})
                }
                onClose();
                showSuccessNotification("Prenotazione aggiornata con successo");
            } catch (e) {
                showErrorNotification(e);
            }
        }
    });

    const handleDelete = async () => {
        await bookingItemDelete.mutateAsync(bookingItem.id);
        onClose();
    }

    const title = !!bookingItem ? "Modifica struttura" : "Nuova struttura";

    const submitIsEnabled = !Object.keys(formik.errors).some(x => formik.touched[x]);

    const showDeleteButton = useMemo(
        () => !!bookingItem &&
        booking.bookingItems.length > 1 &&
        booking.checkIns.flatMap(c => c.checkInItems).every(ci => ci.accommodationUnitId !== bookingItem.bookableId),
        [booking, bookingItem])

    return (
        <ResponsiveDialog title={title}
                          isOpen={true}
                          maxWidth="sm"
                          scroll="paper"
                          isLoading={isLoading}
                          showApply={true}
                          applyLabel="Salva"
                          enableApply={submitIsEnabled}
                          onApply={formik.handleSubmit}
                          closeLabel="Cancella"
                          onClose={onClose}>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Autocomplete
                        autoHighlight
                        value={formik.values.accommodationUnit}
                        options={accommodationUnitOptions}
                        groupBy={(o) => o.accommodation.name}
                        getOptionLabel={(o) => o.name}
                        disabled={!booking.isEditable()}
                        onChange={(event, value) => {
                            formik.setFieldValue("accommodationUnit", value)
                        }}
                        renderInput={(params) => (
                            <TextField {...params}
                                       label="Struttura"
                                       fullWidth
                                       required
                                       error={formik.touched.accommodationUnit && Boolean(formik.errors.accommodationUnit)}
                                       helperText={formik.touched.accommodationUnit && formik.errors.accommodationUnit}
                                       sx={{mt: 1}}/>
                        )}/>
                </Grid>

                <Grid item xs={12}>
                    <TextField type="number"
                               label="Importo"
                               fullWidth
                               name="price"
                               value={formik.values.price}
                               onChange={formik.handleChange}
                               error={formik.touched.price && Boolean(formik.errors.price)}
                               helperText={formik.touched.price && formik.errors.price}
                               InputProps={{
                                   startAdornment: <InputAdornment position="start">€</InputAdornment>,
                               }}/>
                </Grid>
            </Grid>
            {
                showDeleteButton && booking.isEditable() &&
                <Box sx={{mt: 3}}>
                    <Button variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleDelete}>
                        Elimina questa struttura
                    </Button>
                </Box>
            }
        </ResponsiveDialog>
    );
};

FormBookingItem.propTypes = {
    booking: PropTypes.object.isRequired,
    bookingItem: PropTypes.object,
    onClose: PropTypes.func.isRequired
}

export default FormBookingItem;